import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AddBusinessPage() {
  const { user, authHeader, refreshUser, applyToken } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    business_name: "",
    business_description: "",
    contact_phone: user?.phone || "",
    website: "",
    preferred_name: "",
    city: user?.city || "",
    state: user?.state || "",
    address: user?.address || "",
    zip_code: user?.zip_code || "",
    provides_mobile_services: false,
    has_storefront: true,
  });
  const [submitting, setSubmitting] = useState(false);
  // Iteration 55 — Mobile Services flag.
  const [mobileServicesEnabled, setMobileServicesEnabled] = useState(false);
  useEffect(() => {
    axios.get(`${API}/platform/config`).then((r) => {
      setMobileServicesEnabled(!!r.data?.mobile_services_enabled);
    }).catch(() => {});
  }, []);

  if (!user) { nav("/login?next=/account/add-business"); return null; }
  if (user.role !== "customer") {
    // Already a business — nothing to do
    nav("/business");
    return null;
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.business_name.trim().length < 2) return toast.error("Please enter a business name.");
    if (form.business_description.trim().length < 10) return toast.error("Give a short (≥ 10 chars) description of what you do.");
    if (!form.contact_phone.trim()) return toast.error("Contact phone is required.");
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/customer/upgrade-to-business`, form, { headers: authHeader() });
      if (r.data?.token && applyToken) {
        // Fresh token with role=business
        applyToken(r.data.token);
      }
      if (refreshUser) await refreshUser();
      toast.success("Your business is set up! Let's finish your profile.");
      nav("/business");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add your business");
    } finally {
      setSubmitting(false);
    }
  };

  const input = "w-full bg-transparent border rounded-md px-3 py-2 text-sm outline-none";
  const inputStyle = { borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" };

  return (
    <div className="App">
      <Header />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <div className="text-xs uppercase tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>Add your business</div>
        <h1 className="font-display text-3xl sm:text-4xl mt-2" style={{ color: "var(--gf-text)" }}>
          Turn your account into a BookFreely business
        </h1>
        <p className="mt-2 text-sm" style={{ color: "var(--gf-text-soft)" }}>
          Use the same login you already have. Your bookings, favourites, reviews and messages stay put — we just add a business side to your account so customers can find and book you.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="add-business-form">
          <div>
            <label className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Business name</label>
            <input data-testid="add-biz-name" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} required className={input} style={inputStyle} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>What does your business do?</label>
            <textarea data-testid="add-biz-description" rows={3} value={form.business_description} onChange={(e) => set("business_description", e.target.value)} required minLength={10} className={input} style={inputStyle} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Contact phone</label>
              <input data-testid="add-biz-phone" value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} required className={input} style={inputStyle} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Preferred display name (optional)</label>
              <input value={form.preferred_name} onChange={(e) => set("preferred_name", e.target.value)} className={input} style={inputStyle} />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Website (optional)</label>
            <input type="url" value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://…" className={input} style={inputStyle} />
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>City</label>
              <input value={form.city} onChange={(e) => set("city", e.target.value)} className={input} style={inputStyle} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>State</label>
              <input value={form.state} onChange={(e) => set("state", e.target.value)} className={input} style={inputStyle} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>ZIP</label>
              <input value={form.zip_code} onChange={(e) => set("zip_code", e.target.value)} className={input} style={inputStyle} />
            </div>
          </div>
          <div className="flex flex-wrap gap-6 pt-1">
            {mobileServicesEnabled ? (
              <>
                <label className="flex items-center gap-2 text-sm" style={{ color: "var(--gf-text)" }}>
                  <input data-testid="add-biz-mobile" type="checkbox" checked={form.provides_mobile_services} onChange={(e) => set("provides_mobile_services", e.target.checked)} />
                  I provide mobile services (I travel to customers)
                </label>
                {form.provides_mobile_services && (
                  <label className="flex items-center gap-2 text-sm" style={{ color: "var(--gf-text)" }}>
                    <input type="checkbox" checked={form.has_storefront} onChange={(e) => set("has_storefront", e.target.checked)} />
                    I also have a storefront customers can visit
                  </label>
                )}
              </>
            ) : (
              // Iteration 55 — Mobile Services temporarily disabled.
              // Businesses can list their storefront service today; the mobile
              // toggle unlocks once the feature launches.
              null
            )}
          </div>
          <div className="pt-3 flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={submitting}
              data-testid="add-biz-submit"
              className="rounded-full px-6 py-3 text-sm font-medium disabled:opacity-50"
              style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
            >
              {submitting ? "Setting up…" : "Add my business"}
            </button>
            <button
              type="button"
              onClick={() => nav("/account")}
              className="rounded-full border px-6 py-3 text-sm"
              style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
            >
              Cancel
            </button>
          </div>
        </form>
      </main>
      <Footer />
    </div>
  );
}
